/**
 * BildKandidatenListe — die Sichtpruefung der Bild-URLs vor dem Enrichment.
 *
 * @description
 *   Der Freitext bleibt die EINGABE; diese Liste ist die AUSWAHL. Sie zeigt je
 *   URL eine Zeile mit Haken, der im Dateinamen erkannten Farbe und einem
 *   Hinweis, wenn die Farbe nicht zur angebotenen Variante passt. Bei
 *   unbekannter Variantenfarbe erscheint der Hinweis und ein Farbfeld je
 *   Variante. Es wird NICHTS still verworfen: eine abgewaehlte Zeile bleibt
 *   sichtbar, sie geht nur nicht in die Nutzlast (E1/B14).
 *
 *   🔴 Der Farbvergleich liegt NICHT hier: `POST
 *   /api/enrichment/bildkandidaten-pruefen` ist der eine Ort des Lexikons
 *   (kein zweites JS-Lexikon). Die Komponente rendert nur die Antwort.
 *
 * @useWhen
 *   - Ein Enrichment-Auftrag soll Bild-URLs pruefen, bevor er sie sendet
 *   - Eine Zeilenliste mit Haken je URL und erkannter Farbe gebraucht wird
 *   - Die Variantenfarbe unbekannt ist und je Variante gesetzt werden soll
 *
 * @dontUseFor
 *   - Eine reine Textliste ohne Auswahl — dafuer reicht ein `TextField`
 *   - Das Filternd von URLs: POG und n8n filtern nicht (E1)
 *   - Andere Pruefungen als Bildfarben (Taxonomie, Preise)
 *
 * @related
 *   - `EnrichmentDialog` — erster Aufrufer (PDP „AI update…")
 *   - `UnifiedCreateWizard` — zweiter Aufrufer (Produktanlage)
 *   - `Pill` — Hausbaustein fuer das Farbetikett (R16-W4: wirklich benutzt)
 *
 * @reuseHints
 *   - Eine spaetere Sammelaktion ueber mehrere Produkte koennte dieselbe Liste
 *     je Produkt rendern
 *
 * @props
 *   urls              — die Zeilen aus dem Freitextfeld (eine URL je Zeile);
 *                       Duplikate werden VOR dem Senden entfernt (R16-W1)
 *   varianten         — Variantenzeilen (`sku`, `variant_name`, …) oder bereits
 *                       aufgeloest (`farbe`, `farbquelle`)
 *   variantenFehler   — Grund, warum die Varianten NICHT geladen wurden
 *                       (`null` = geladen). R16-W2: „keine Varianten" und
 *                       „nicht geladen" sind zwei verschiedene Zustaende
 *   onVariantenErneut — optionaler Wiederholer fuer den Variantenabruf
 *   onAuswahlChange   — erhaelt die ausgewaehlten URLs, sobald sie sich aendern
 *   onFarbenChange    — erhaelt `{sku: farbe}` aus den Farbfeldern; die Wahl
 *                       geht in den NAECHSTEN Pruefaufruf (R15-W1)
 *   verzoegerungMs    — Entprellung der Pruefroute (Vorgabe 400 ms)
 */
import React, {
  useCallback, useEffect, useId, useMemo, useRef, useState,
} from 'react';
import {
  Alert, Box, Button, Checkbox, FormControlLabel, MenuItem, Skeleton,
  Stack, TextField, Typography,
} from '@mui/material';
import { api } from '../../api/client';
import { FS, SP } from '../../theme/tokens';
import Pill from './Pill';

const PRUEFROUTE = '/api/enrichment/bildkandidaten-pruefen';

function dateiname(url) {
  const ohneQuery = String(url || '').split('?')[0];
  return ohneQuery.split('/').pop() || String(url || '');
}

function ZeilenHinweis({ zeile, angeboteneFarben }) {
  if (zeile.hinweis !== 'farbe_nicht_angeboten') return null;
  const angebot = (angeboteneFarben || []).join(', ');
  return (
    <Typography sx={{ fontSize: FS.xs, color: 'warning.main' }}>
      {`Image colour ${zeile.farbe || '—'} is not offered by this variant`}
      {angebot ? ` (${angebot}).` : '.'}
    </Typography>
  );
}

export default function BildKandidatenListe({
  urls,
  varianten,
  variantenFehler = null,
  onVariantenErneut,
  onAuswahlChange,
  onFarbenChange,
  verzoegerungMs = 400,
}) {
  // `useMemo` gegen die Warnung „conditional could make the dependencies of
  // useCallback change on every render" (CRA-Bau: Warnungen sind Fehler).
  //
  // 🔴 R16-W1: DUPLIKATE fallen hier heraus, vor dem Senden. Der Server
  // spiegelt je Eingabe-URL genau eine Zeile — zwei gleiche URLs ergaeben zwei
  // React-Zeilen mit demselben Schluessel (Warnung, Fehlzuordnung beim
  // Umschalten) und zwei Haken, die zusammen haengen. Dedupe und Index im
  // Schluessel sind zwei Gurte, nicht zwei Loesungen.
  const zeilenUrls = useMemo(() => {
    const roh = Array.isArray(urls) ? urls : [];
    const gesehen = new Set();
    return roh.filter((url) => {
      const schluessel = String(url);
      if (gesehen.has(schluessel)) return false;
      gesehen.add(schluessel);
      return true;
    });
  }, [urls]);
  const variantenListe = useMemo(
    () => (Array.isArray(varianten) ? varianten : []), [varianten]);
  const titelId = useId();

  const [daten, setDaten] = useState(null);
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState(null);
  const [auswahl, setAuswahl] = useState([]);
  const [farben, setFarben] = useState({});
  const [versuch, setVersuch] = useState(0);
  const beruehrt = useRef(new Set());

  // Die Rueckrufe sind fuer den Elternteil stabil gedacht; ein Wechsel der
  // Funktionsidentitaet darf die Pruefung nicht neu starten.
  const rueckrufe = useRef({ onAuswahlChange, onFarbenChange });
  rueckrufe.current = { onAuswahlChange, onFarbenChange };

  const urlsKey = zeilenUrls.join('\n');
  const variantenKey = JSON.stringify(variantenListe);
  // 🔴 R15-W1: die gewaehlte Variantenfarbe ist EINGABE der Pruefung. Ohne sie
  // im Schluessel bliebe das Banner „Variant colour unknown" stehen, obwohl der
  // Nutzer gerade eine Farbe gesetzt hat — der beworbene Ablauf endete ohne
  // Rueckmeldung.
  const farbenKey = JSON.stringify(farben);

  useEffect(() => {
    if (!zeilenUrls.length) {
      setDaten(null);
      setLaedt(false);
      return undefined;
    }
    let abgemeldet = false;
    setLaedt(true);
    const timer = setTimeout(() => {
      Promise.resolve(api.post(PRUEFROUTE, {
        varianten: variantenListe, urls: zeilenUrls, farben,
      }))
        .then((antwort) => {
          if (abgemeldet) return;
          setDaten(antwort?.data || null);
          setFehler(null);
        })
        .catch((e) => {
          if (abgemeldet) return;
          setFehler(e?.message || 'Unknown error');
        })
        .finally(() => {
          if (!abgemeldet) setLaedt(false);
        });
    }, verzoegerungMs);
    return () => {
      abgemeldet = true;
      clearTimeout(timer);
    };
    // `urlsKey`/`variantenKey`/`farbenKey` sind die Inhalte, nicht die
    // Array-Identitaet.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlsKey, variantenKey, farbenKey, verzoegerungMs, versuch]);

  // Die Standardauswahl kommt aus der Antwort: `standard_aus` ist heute immer
  // false (M1 = 2,4 % — E1-Widerleger), aber der Weg bleibt gebaut.
  useEffect(() => {
    if (!daten) return;
    const zeilen = Array.isArray(daten.urls) ? daten.urls : [];
    const neu = zeilen
      .filter((z) => (beruehrt.current.has(z.url)
        ? auswahl.includes(z.url)
        : !z.standard_aus))
      .map((z) => z.url);
    setAuswahl(neu);
    rueckrufe.current.onAuswahlChange?.(neu);
    // Nur die ANTWORT aendert die Vorauswahl; `auswahl` ist der Stand davor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [daten]);

  const umschalten = useCallback((url) => (event) => {
    beruehrt.current.add(url);
    const gewaehlt = event.target.checked;
    const neu = zeilenUrls.filter((u) => (u === url ? gewaehlt : auswahl.includes(u)));
    setAuswahl(neu);
    rueckrufe.current.onAuswahlChange?.(neu);
    // `zeilenUrls` und `auswahl` sind der aktuelle Stand beim Klick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zeilenUrls, auswahl]);

  const setzeFarbe = useCallback((sku) => (event) => {
    const neu = { ...farben, [sku]: event.target.value };
    if (!neu[sku]) delete neu[sku];
    setFarben(neu);
    rueckrufe.current.onFarbenChange?.(neu);
  }, [farben]);

  if (!zeilenUrls.length) return null;

  const zeilen = Array.isArray(daten?.urls) ? daten.urls : [];
  const farbenListe = Array.isArray(daten?.farben) ? daten.farben : [];
  const unbekannt = daten?.farbe_bekannt === false;

  return (
    <Box sx={{ mt: SP.chip }} data-testid="bildkandidaten-liste">
      {/* R16-W3: eine echte Ueberschrift, keine typografierte Zeile — die
          Liste darunter verweist per `aria-labelledby` darauf. */}
      <Typography id={titelId} component="h6"
        sx={{ fontSize: FS.sm, fontWeight: 600, mb: SP.chip }}>
        Image candidates
      </Typography>

      {variantenFehler && (
        <Alert
          severity="warning"
          sx={{ mb: SP.chip }}
          action={onVariantenErneut ? (
            <Button size="small" onClick={onVariantenErneut}>Retry</Button>
          ) : undefined}
        >
          {`Variants could not be loaded. ${variantenFehler}`}
        </Alert>
      )}

      {laedt && (
        <Stack spacing={SP.tight} aria-busy="true">
          <Skeleton variant="rounded" height={28} />
          <Skeleton variant="rounded" height={28} />
        </Stack>
      )}

      {!laedt && fehler && (
        <Alert
          severity="warning"
          action={(
            <Button size="small" onClick={() => setVersuch((v) => v + 1)}>
              Retry
            </Button>
          )}
        >
          {`Image check failed. ${fehler}`}
        </Alert>
      )}

      {!laedt && !fehler && unbekannt && (
        <Alert severity="info" sx={{ mb: SP.chip }}>
          Variant colour unknown — check the images.
        </Alert>
      )}
      {!laedt && !fehler && daten?.verschiedene_farben && (
        <Alert severity="warning" sx={{ mb: SP.chip }}>
          Images show different colours — check the variant.
        </Alert>
      )}

      {!laedt && !fehler && zeilen.length > 0 && (
        <Box component="ul" aria-labelledby={titelId}
          sx={{ listStyle: 'none', m: 0, p: 0 }}>
          {zeilen.map((zeile, index) => (
            <Box component="li" key={`${zeile.url}-${index}`}
              sx={{ py: SP.chip }}>
              <FormControlLabel
                control={(
                  <Checkbox
                    size="small"
                    checked={auswahl.includes(zeile.url)}
                    onChange={umschalten(zeile.url)}
                  />
                )}
                label={(
                  <Stack>
                    <Typography sx={{ fontSize: FS.xs, wordBreak: 'break-all' }}>
                      {dateiname(zeile.url)}
                    </Typography>
                    {zeile.farbe && (
                      <Pill data-testid="bildkandidaten-farbe">
                        {zeile.farbe}
                      </Pill>
                    )}
                  </Stack>
                )}
              />
              <ZeilenHinweis zeile={zeile} angeboteneFarben={daten?.angebotene_farben} />
            </Box>
          ))}
        </Box>
      )}

      {!laedt && !fehler && unbekannt && farbenListe.length > 0 && variantenListe.length > 0 && (
        <Stack spacing={SP.chip} sx={{ mt: SP.chip }}>
          {variantenListe.map((variante, index) => {
            const sku = String(variante?.sku || `variante-${index}`);
            return (
              <TextField
                key={sku}
                select
                size="small"
                label={`Variant colour${variante?.sku ? ` (${sku})` : ''}`}
                value={farben[sku] || ''}
                onChange={setzeFarbe(sku)}
              >
                <MenuItem value="">—</MenuItem>
                {farbenListe.map((farbe) => (
                  <MenuItem key={farbe} value={farbe}>{farbe}</MenuItem>
                ))}
              </TextField>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}
